// utils/tagTitleMemory.js
//
// ITEM-TAG TITLES, REMEMBERED FOR THE SESSION (docs/plans/tags.md §3.2). An
// item tag stores no name: it is named by its item's title, and only to
// someone who can read the item. A picked item tag must keep reading as that
// title — in a chip, a builder row, a filter chip — after the search that
// found it is gone, so each title this reader was SHOWN (a tag search's
// answer, an item read's `tagTitles`) is kept here. Nothing is ever learned
// that the reader's own scope did not hand over, and the memory is dropped
// when another account (or none) signs in on the tab.

import { normalizeId } from './ids';
import { onSessionChange } from './session';

// tagId -> { title, item_id, card_id, collection_id? }
const titles = new Map();

onSessionChange(() => { titles.clear(); });

/** Remember an item tag's title (as a search, or an item's `tagTitles`, showed it). */
export function rememberItemTagTitle(tagId, entry) {
	const id = normalizeId(tagId);
	if (!id || !entry || typeof entry.title !== 'string') return;
	titles.set(id, {
		title: entry.title,
		item_id: normalizeId(entry.item_id) || null,
		card_id: normalizeId(entry.card_id) || null,
		...(entry.collection_id ? { collection_id: normalizeId(entry.collection_id) } : {}),
	});
}

/** Every item-tag title an item read carries (`tagTitles`), remembered at once. */
export function rememberItemTagTitles(items = []) {
	(Array.isArray(items) ? items : [items]).forEach(item => {
		const map = item?.tagTitles;
		if (!map || typeof map !== 'object') return;
		Object.entries(map).forEach(([id, entry]) => {
			if (entry && typeof entry.title === 'string') rememberItemTagTitle(id, entry);
		});
	});
}

/**
 * The item-tag titles a list of item reads carries, as one `{ id: entry }`
 * object (remembered as it is read) — what a page hands its `tags` filter
 * field, so a chip naming an item tag reads its title the moment the feed
 * holds an item carrying it.
 */
export function itemTagTitlesOf(items = []) {
	const out = {};
	(Array.isArray(items) ? items : []).forEach(item => {
		const map = item?.tagTitles;
		if (!map || typeof map !== 'object') return;
		Object.entries(map).forEach(([id, entry]) => {
			if (entry && typeof entry.title === 'string') out[normalizeId(id)] = entry;
		});
	});
	Object.entries(out).forEach(([id, entry]) => rememberItemTagTitle(id, entry));
	return out;
}

/** A remembered item tag's title entry, or undefined. */
export const itemTagTitleFor = tagId => titles.get(normalizeId(tagId));

/** Every remembered title, as a plain `{ id: entry }` object. */
export const rememberedItemTagTitles = () => Object.fromEntries(titles.entries());

/** Test seam. */
export const forgetItemTagTitles = () => titles.clear();
